/**
 * MIT License
 *
 * Copyright (c) Sindre Sorhus sindresorhus[at]gmail.com (https://sindresorhus.com)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
// GIF
const isGif = (bytes: Uint8Array) =>
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x38 &&
    (bytes[4] === 0x37 || bytes[4] === 0x39) &&
    bytes[5] === 0x61;

function gif(bytes: Uint8Array) {
    if (!isGif(bytes)) {
        return undefined;
    }

    const dataView = new DataView(bytes.buffer);

    const width = getUint16(dataView, 6, true);
    const height = getUint16(dataView, 8, true);

    if (width === undefined || height === undefined) {
        return undefined;
    }

    return {
        width,
        height,
    };
}

const SOF0 = 0xff_c0;
const SOF3 = 0xff_c3;

// JPG/JPEG
const isJpeg = (bytes: Uint8Array) =>
    bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;

function jpeg(bytes: Uint8Array) {
    if (!isJpeg(bytes)) {
        return undefined;
    }

    const dataView = new DataView(bytes.buffer);

    let offset = 2; // Start after the SOI marker.

    while (isValidOffsetToRead(dataView, offset, 2)) {
        const marker = dataView.getUint16(offset);
        offset += 2; // Move past the marker.

        if (marker >= SOF0 && marker <= SOF3) {
            const height = getUint16(dataView, offset + 3, false);
            const width = getUint16(dataView, offset + 5, false);

            if (height === undefined || width === undefined) {
                return undefined;
            }

            return {
                height,
                width,
            };
        }

        const segmentLength = getUint16(dataView, offset);

        if (segmentLength === undefined) {
            return undefined; // Unexpected EOF when reading segment length.
        }

        offset += segmentLength; // Skip over the segment.

        if (offset > dataView.byteLength) {
            return undefined; // Segment length exceeds byte range.
        }
    }

    return undefined;
}

// PNG
const isPng = (bytes: Uint8Array) =>
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a;

const isAppleMinifiedPng = (bytes: Uint8Array) =>
    bytes[12] === 0x43 &&
    bytes[13] === 0x67 &&
    bytes[14] === 0x42 &&
    bytes[15] === 0x49;

export default function png(bytes: Uint8Array) {
    if (!isPng(bytes)) {
        return undefined;
    }

    const dataView = new DataView(bytes.buffer);
    const isAppleMinified = isAppleMinifiedPng(bytes);

    const width = getUint32(dataView, isAppleMinified ? 32 : 16, false);
    const height = getUint32(dataView, isAppleMinified ? 36 : 20, false);

    if (width === undefined || height === undefined) {
        return undefined;
    }

    return {
        width,
        height,
    };
}

// WEBP
const isWebp = (bytes: Uint8Array) =>
    // RIFF
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    // WEBP
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50;

const isVP8Lossy = (bytes: Uint8Array) =>
    // `VP8 ` (note the space)
    bytes[12] === 0x56 &&
    bytes[13] === 0x50 &&
    bytes[14] === 0x38 &&
    bytes[15] === 0x20;

const isVP8Lossless = (bytes: Uint8Array) =>
    // `VP8L`
    bytes[12] === 0x56 &&
    bytes[13] === 0x50 &&
    bytes[14] === 0x38 &&
    bytes[15] === 0x4c;

const isVP8Extended = (bytes: Uint8Array) =>
    // `VP8X`
    bytes[12] === 0x56 &&
    bytes[13] === 0x50 &&
    bytes[14] === 0x38 &&
    bytes[15] === 0x58;

function readUInt24LE(dataView: DataView, offset: number) {
    const byte1 = dataView.getUint8(offset);
    const byte2 = dataView.getUint8(offset + 1);
    const byte3 = dataView.getUint8(offset + 2);

    // Combine the three bytes into a 24-bit integer.
    // eslint-disable-next-line no-bitwise
    return (byte3 << 16) | (byte2 << 8) | byte1;
}

function webp(bytes: Uint8Array) {
    if (!isWebp(bytes)) {
        return undefined;
    }

    const dataView = new DataView(bytes.buffer);
    const maxSize = 0x3f_ff;

    if (isVP8Lossy(bytes)) {
        if (!isValidOffsetToRead(dataView, 28, 2)) {
            return undefined;
        }

        return {
            // eslint-disable-next-line no-bitwise
            width: dataView.getUint16(26, true) & maxSize,
            // eslint-disable-next-line no-bitwise
            height: dataView.getUint16(28, true) & maxSize,
        };
    }

    if (isVP8Lossless(bytes)) {
        const bits = getUint32(dataView, 21, true);

        if (bits === undefined) {
            return undefined;
        }

        return {
            // eslint-disable-next-line no-bitwise
            width: (bits & maxSize) + 1,
            // eslint-disable-next-line no-bitwise
            height: ((bits >> 14) & maxSize) + 1,
        };
    }

    if (isVP8Extended(bytes)) {
        if (!isValidOffsetToRead(dataView, 27, 3)) {
            return undefined;
        }

        return {
            width: readUInt24LE(dataView, 24) + 1,
            height: readUInt24LE(dataView, 27) + 1,
        };
    }

    return undefined;
}

function isValidOffsetToRead(
    dataView: DataView,
    offset: number,
    bytesToRead: number
) {
    return dataView.byteLength >= offset + bytesToRead;
}

function getUint32(
    dataView: DataView,
    offset: number,
    littleEndian: boolean = false
) {
    if (isValidOffsetToRead(dataView, offset, 4)) {
        return dataView.getUint32(offset, littleEndian);
    }

    return undefined;
}

function getUint16(
    dataView: DataView,
    offset: number,
    littleEndian: boolean = false
) {
    if (isValidOffsetToRead(dataView, offset, 2)) {
        return dataView.getUint16(offset, littleEndian);
    }

    return undefined;
}

function getImageDimensions(bytes: ArrayBuffer) {
    const bytesContent = new Uint8Array(bytes);

    return (
        png(bytesContent) ??
        gif(bytesContent) ??
        jpeg(bytesContent) ??
        webp(bytesContent)
    );
}

function getImageMime(bytes: ArrayBuffer) {
    const bytesContent = new Uint8Array(bytes);

    return isPng(bytesContent)
        ? "image/png"
        : isGif(bytesContent)
          ? "image/gif"
          : isJpeg(bytesContent)
            ? "image/jpg"
            : isWebp(bytesContent)
              ? "image/webp"
              : null;
}

export { getImageDimensions, getImageMime };
